import BillingsTaxiDriverDetailsResponse, {
  BillingsTaxiDriverDetailsResponseContractModel,
  BillingsTaxiDriverDetailsResponseData,
  BillingsTaxiDriverDetailsResponseDataBillingNode,
  BillingsTaxiDriverDetailsResponseDataCargoOrder,
  BillingsTaxiDriverDetailsResponseDataContract,
  BillingsTaxiDriverDetailsResponseDataContribution,
  BillingsTaxiDriverDetailsResponseDataContributionType,
  BillingsTaxiDriverDetailsResponseDataDriver,
  BillingsTaxiDriverDetailsResponseDataDriverAddress,
  BillingsTaxiDriverDetailsResponseDataDriverUser,
  BillingsTaxiDriverDetailsResponseDataFleetPartner,
  BillingsTaxiDriverDetailsResponseDataPlanEntry,
  BillingsTaxiDriverDetailsResponseDataSolvedOrder,
  BillingsTaxiDriverDetailsResponseDataTaxiDriverAssociation,
  BillingsTaxiDriverDetailsResponseDataTransportingOrder,
} from "../../../../api/raily/billings/taxi-driver/details/billings-taxi-driver-details.response";
import BillingModel from "../../../../types/billing-model";
import BillingsTaxiDriverDetails, {
  BillingsTaxiDriverDetailsBillingNode,
  BillingsTaxiDriverDetailsCargoOrder,
  BillingsTaxiDriverDetailsContract,
  BillingsTaxiDriverDetailsContribution,
  BillingsTaxiDriverDetailsContributionType,
  BillingsTaxiDriverDetailsDriver,
  BillingsTaxiDriverDetailsDriverAddress,
  BillingsTaxiDriverDetailsDriverUser,
  BillingsTaxiDriverDetailsFleetPartner,
  BillingsTaxiDriverDetailsPlanEntry,
  BillingsTaxiDriverDetailsSolvedOrder,
  BillingsTaxiDriverDetailsTaxiDriverAssociation,
  BillingsTaxiDriverDetailsTransportingOrder,
} from "./billings-taxi-driver-details";

const createContributionType = (
  type:
    | BillingsTaxiDriverDetailsResponseDataContributionType.BONUS_OTHER
    | BillingsTaxiDriverDetailsResponseDataContributionType.PENALTY_WRONG_ROUTE
    | BillingsTaxiDriverDetailsResponseDataContributionType.PENALTY_BEING_LATE
    | BillingsTaxiDriverDetailsResponseDataContributionType.PENALTY_INCOMPATIBLE_CAR
    | BillingsTaxiDriverDetailsResponseDataContributionType.PENALTY_OTHER,
):
  | BillingsTaxiDriverDetailsContributionType.BONUS_OTHER
  | BillingsTaxiDriverDetailsContributionType.PENALTY_WRONG_ROUTE
  | BillingsTaxiDriverDetailsContributionType.PENALTY_BEING_LATE
  | BillingsTaxiDriverDetailsContributionType.PENALTY_INCOMPATIBLE_CAR
  | BillingsTaxiDriverDetailsContributionType.PENALTY_OTHER => {
  switch (type) {
    case BillingsTaxiDriverDetailsResponseDataContributionType.BONUS_OTHER:
      return BillingsTaxiDriverDetailsContributionType.BONUS_OTHER;
    case BillingsTaxiDriverDetailsResponseDataContributionType.PENALTY_WRONG_ROUTE:
      return BillingsTaxiDriverDetailsContributionType.PENALTY_WRONG_ROUTE;
    case BillingsTaxiDriverDetailsResponseDataContributionType.PENALTY_BEING_LATE:
      return BillingsTaxiDriverDetailsContributionType.PENALTY_BEING_LATE;
    case BillingsTaxiDriverDetailsResponseDataContributionType.PENALTY_INCOMPATIBLE_CAR:
      return BillingsTaxiDriverDetailsContributionType.PENALTY_INCOMPATIBLE_CAR;
    case BillingsTaxiDriverDetailsResponseDataContributionType.PENALTY_OTHER:
      return BillingsTaxiDriverDetailsContributionType.PENALTY_OTHER;
  }
};

const createContribution = (
  contribution: BillingsTaxiDriverDetailsResponseDataContribution,
): BillingsTaxiDriverDetailsContribution => {
  switch (contribution.type) {
    case BillingsTaxiDriverDetailsResponseDataContributionType.BASE_AMOUNT_EXTERNAL_TAXI:
      return {
        type: BillingsTaxiDriverDetailsContributionType.BASE_AMOUNT_EXTERNAL_TAXI,
        amount: contribution.amount,
        comment: contribution.comment,
        distance: contribution.distance,
      };
    case BillingsTaxiDriverDetailsResponseDataContributionType.CONTRACT_CORRECTION_RATE:
      return {
        type: BillingsTaxiDriverDetailsContributionType.CONTRACT_CORRECTION_RATE,
        comment: contribution.comment,
        rate: contribution.rate,
      };
    case BillingsTaxiDriverDetailsResponseDataContributionType.BONUS_FAVORABLE_DISTANCE:
      return {
        type: BillingsTaxiDriverDetailsContributionType.BONUS_FAVORABLE_DISTANCE,
        comment: contribution.comment,
        distance: contribution.distance,
        rate: contribution.rate,
      };
    case BillingsTaxiDriverDetailsResponseDataContributionType.BONUS_OTHER:
    case BillingsTaxiDriverDetailsResponseDataContributionType.PENALTY_BEING_LATE:
    case BillingsTaxiDriverDetailsResponseDataContributionType.PENALTY_INCOMPATIBLE_CAR:
    case BillingsTaxiDriverDetailsResponseDataContributionType.PENALTY_OTHER:
    case BillingsTaxiDriverDetailsResponseDataContributionType.PENALTY_WRONG_ROUTE:
      return {
        type: createContributionType(contribution.type),
        comment: contribution.comment,
        amount: contribution.amount,
      };
  }
};

const createContributions = (
  contributions: BillingsTaxiDriverDetailsResponseDataContribution[],
): BillingsTaxiDriverDetailsContribution[] => {
  return contributions
    .map(createContribution)
    .filter((contribution) => contribution);
};

const createNode = (
  node: BillingsTaxiDriverDetailsResponseDataBillingNode,
): BillingsTaxiDriverDetailsBillingNode => {
  return {
    allowCharge: node.allowCharge,
    checkoutDate: node.checkoutDate,
    checkoutEventId: node.checkoutEventId,
    description: node.description,
    distance: node.distance,
    editableCoordinates: node.editableCoordinates,
    haltingTime: node.haltingTime,
    highwayCharge: node.highwayCharge,
    lat: node.lat,
    lon: node.lon,
    nodeId: node.nodeId,
    orderId: node.orderId,
    plannedDate: node.plannedDate,
    plannedDistance: node.plannedDistance,
    id: node.id,
  };
};

const createNodes = (
  nodes: BillingsTaxiDriverDetailsResponseDataBillingNode[],
): BillingsTaxiDriverDetailsBillingNode[] => {
  return nodes.map(createNode);
};

const createModel = (
  model: BillingsTaxiDriverDetailsResponseContractModel,
): BillingModel => {
  switch (model) {
    case BillingsTaxiDriverDetailsResponseContractModel.AB:
      return BillingModel.AB;
    case BillingsTaxiDriverDetailsResponseContractModel.ABA:
      return BillingModel.ABA;
    case BillingsTaxiDriverDetailsResponseContractModel.OTHER:
      return BillingModel.OTHER;
    case BillingsTaxiDriverDetailsResponseContractModel.SABS:
      return BillingModel.SABS;
  }
};

const createDriverAddress = (
  address: BillingsTaxiDriverDetailsResponseDataDriverAddress,
): BillingsTaxiDriverDetailsDriverAddress => {
  return {
    displayName: address.displayName,
  };
};

const createDriverUser = (
  user: BillingsTaxiDriverDetailsResponseDataDriverUser,
): BillingsTaxiDriverDetailsDriverUser => {
  return {
    firstName: user.firstName,
    lastName: user.lastName,
  };
};

const createDriver = (
  driver: BillingsTaxiDriverDetailsResponseDataDriver,
): BillingsTaxiDriverDetailsDriver => {
  return {
    startingAddress: createDriverAddress(driver.startingAddress),
    user: createDriverUser(driver.user),
  };
};

const createFleetPartner = (
  fleetPartner: BillingsTaxiDriverDetailsResponseDataFleetPartner | null,
): BillingsTaxiDriverDetailsFleetPartner | null => {
  if (!fleetPartner) return null;

  return {
    name: fleetPartner.name,
  };
};

const createTaxiDriverAssociation = (
  association: BillingsTaxiDriverDetailsResponseDataTaxiDriverAssociation,
): BillingsTaxiDriverDetailsTaxiDriverAssociation => {
  return {
    driver: createDriver(association.driver),
    fleetPartner: createFleetPartner(association.fleetPartner),
  };
};

const createContract = (
  contract: BillingsTaxiDriverDetailsResponseDataContract,
): BillingsTaxiDriverDetailsContract => {
  return {
    description: contract.description,
    distanceRate: contract.distanceRate,
    haltingTimeAfterMinutes: contract.haltingTimeAfterMinutes,
    haltingTimeRate: contract.haltingTimeRate,
    model: createModel(contract.model),
    taxiDriverAssociation: createTaxiDriverAssociation(
      contract.taxiDriverAssociation,
    ),
  };
};

const createCargoOrder = (
  cargoOrder: BillingsTaxiDriverDetailsResponseDataCargoOrder,
): BillingsTaxiDriverDetailsCargoOrder => {
  return {
    humanId: cargoOrder.humanId,
    id: cargoOrder.id,
  };
};

const createTransportingOrder = (
  transportingOrder: BillingsTaxiDriverDetailsResponseDataTransportingOrder,
): BillingsTaxiDriverDetailsTransportingOrder => {
  return {
    cargoOrder: createCargoOrder(transportingOrder.cargoOrder),
  };
};

const createSolvedOrder = (
  solvedOrder: BillingsTaxiDriverDetailsResponseDataSolvedOrder,
): BillingsTaxiDriverDetailsSolvedOrder => {
  return {
    transportingOrder: createTransportingOrder(solvedOrder.transportingOrder),
  };
};

const createSolvedOrders = (
  solvedOrders: BillingsTaxiDriverDetailsResponseDataSolvedOrder[],
): BillingsTaxiDriverDetailsSolvedOrder[] => {
  return solvedOrders.map(createSolvedOrder);
};

const createPlanEntry = (
  planEntry: BillingsTaxiDriverDetailsResponseDataPlanEntry,
): BillingsTaxiDriverDetailsPlanEntry => {
  return {
    humanId: planEntry.humanId,
    solvedOrders: createSolvedOrders(planEntry.solvedOrders),
    id: planEntry.id,
  };
};

const createData = (
  data: BillingsTaxiDriverDetailsResponseData,
): BillingsTaxiDriverDetails => {
  return {
    allContributionsAmount: data.allContributionsAmount,
    amountForChargeHaltings: data.amountForChargeHaltings,
    amountForChargeHighways: data.amountForChargeHighways,
    amountForDistance: data.amountForDistance,
    billingContributions: createContributions(data.billingContributions),
    billingNodes: createNodes(data.billingNodes),
    contract: createContract(data.contract),
    correctedContractModel: createModel(data.correctedContractModel),
    date: data.date,
    distance: data.distance,
    passengers: data.passengers,
    planEntry: createPlanEntry(data.planEntry),
    rateForDistance: data.rateForDistance,
    sumOfBonuses: data.sumOfBonuses,
    sumOfPenalties: data.sumOfPenalties,
  };
};

const create = (
  response: BillingsTaxiDriverDetailsResponse,
): BillingsTaxiDriverDetails => {
  return createData(response.data);
};

const billingsTaxiDriverDetailsFactory = {
  create,
};

export default billingsTaxiDriverDetailsFactory;
